import React from 'react'
import * as state from '../../states'

import {
	IRevenueAgency,
	ERoleAccess,
	ERevenueAgencyFakeType,
	TRevenueAgencyHistory,
	REVENUE_AGENCY_FAKE_TYPE_LABEL_MAP
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	UiMoreTextInfo,
	headCellFormatterSortable,
	ESortOrder,
	CheckBoxStatusIcon,
	HistoryIconBtn,
	NotesIconBtn
} from '../ui'
import { useAtomValue } from 'jotai'
import { REVENUE_AGENCY_PAGE_SORT_FIELDS, IRevenueAgencySortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import dt from 'dayjs'
import styled from 'styled-components'
import { REVENUE_AGENCY_FAKE_TYPE_COLORS_MAP } from '../../constants'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IRevenueAgencyListTableProps = {
	items: IRevenueAgency[];
	skip: number;
	sortBy: IRevenueAgencySortField;
	setSortBy: (sortBy: IRevenueAgencySortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IRevenueAgency, e: React.MouseEvent) => void;
	onEditNotes?: (item: IRevenueAgency, e: React.MouseEvent) => void;
	onRemove?: (item: IRevenueAgency, e: React.MouseEvent) => void;
	onViewHistory?: (item: IRevenueAgency, e: React.MouseEvent) => void;
}

type IRevenueAgencyFakeTypeProps = {
	value: ERevenueAgencyFakeType;
	rowData: IRevenueAgency | TRevenueAgencyHistory; // { }
	onClick?: (rowData: IRevenueAgency | TRevenueAgencyHistory, e: React.MouseEvent) => void;
	classNamesToAdd?: string;
}

export const RevenueAgencyFakeTypeField = (props: IRevenueAgencyFakeTypeProps) => {
	const { t } = useTranslation()
	const { value, classNamesToAdd, rowData, onClick } = props

	const keyTrans = REVENUE_AGENCY_FAKE_TYPE_LABEL_MAP?.[value]
	const labelTrans = t(keyTrans)

	return <>
		<span
			/*style={{cursor: 'pointer'}}*/
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			onClick={(e) => onClick && onClick(rowData, e)}
		>
			{labelTrans}
		</span>
	</>
}

type IYesNoField = {
	value: boolean
}
const YesNoField = (props: IYesNoField) => {
	const { value } = props

	const classNamesToAdd = value ? 'bg-success' : 'bg-danger'

	return <>
		<span
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
		>
			{value ? 'Yes' : 'No'}
		</span>
	</>
}

export const AgencyPercentBox = styled.div`
    display: inline-flex;
    flex-wrap: nowrap;
    gap: 5px;
`

export const AgencyPercentValue = styled.span`
    display: inline-flex;
    justify-content: flex-end;
    min-width: 75px;
`

export const AgencyPercentInfo = styled.span`
    display: inline-flex;
    justify-content: flex-start;
    min-width: 200px;

    color: #6c757d;
    opacity: 0.65;
`

export const RevenueAgencyListTable = (props: IRevenueAgencyListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onEditNotes,
		onRemove,
		onViewHistory
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const revenueAgencyListTableConfig: IUiTableConfig<IRevenueAgency>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_id)
		},
		{
			key: 'name',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_name)
		},
		{
			key: 'extra.group',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_group),
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = rowData?.extra?.group ?? ''

				return <>
					{formatValue}
				</>
			}
		},
		{
			key: 'percent',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_percent),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const valueFormat: Array<React.ReactNode> = rowData.percents?.map((item, index, arr) => {
					const at = item.at ?? null
					const nextAt = item.nextAt ?? null
					const percent = helpers.formatAmount(item.value)
					const atFormat = dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'
					// subtract 1 day from nextAt date for regular CRM users (not a mathematicians)
					const nextAtFormat = dt.utc(nextAt).isValid() ? dt.utc(nextAt).subtract(1, 'day').format('L') : '∞'

					return <AgencyPercentBox>
						<AgencyPercentValue>{percent}%</AgencyPercentValue>
						<AgencyPercentInfo>
							({atFormat} - {nextAtFormat}){arr.length - 1 !== index ? ',' : null}
						</AgencyPercentInfo>
					</AgencyPercentBox>
				}) ?? []
				// show only 1 default percent in collapsed state
				return <UiMoreTextInfo value={valueFormat}
				                       withSeparator={false}
				                       limitCollapsed={1}
				/>
			}
		},
		{
			key: 'isSystem',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_is_system),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				return <>
					<CheckBoxStatusIcon value={rowData.isSystem} iconCheckColor={'--dangerColor'}/>
				</>
			}
		},
		{
			key: 'isActive',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_is_active),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				return <>
					<CheckBoxStatusIcon value={rowData.isActive}/>
				</>
			}
		},
		{
			key: 'isHold',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_is_hold),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				return <>
					<CheckBoxStatusIcon value={rowData.isHold} iconCheckColor={'--warningColor'}/>
				</>
			}
		},
		{
			key: 'fakeType',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_fake_type),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: ERevenueAgencyFakeType = value ?? ERevenueAgencyFakeType.None

				return <div title={valueFormat}>
					<RevenueAgencyFakeTypeField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={REVENUE_AGENCY_FAKE_TYPE_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on RevenueAgencyFakeType field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		},
		{
			key: 'history.empty',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_history),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.history

				return <>
					{
						onViewHistory && !!$access.$(ERoleAccess.RevenueAgencyList)
							? (
								<HistoryIconBtn value={val}
								                onClick={(e) => onViewHistory && onViewHistory(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.revenue_agency_list_table.column_notes),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.notes

				return <>
					{
						onEditNotes && !!$access.$(ERoleAccess.RevenueAgencyList)
							? (
								<NotesIconBtn value={val}
								              onClick={(e) => onEditNotes && onEditNotes(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				const isRemoveBtnActive = !rowData.isSystem

				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onEdit && !!$access.$(ERoleAccess.RevenueAgencyModify)
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.RevenueAgencyRemove)
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && isRemoveBtnActive && onRemove(rowData, e)}
										disabled={!isRemoveBtnActive}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const revenueAgencyListTableConfigSortable: IUiTableConfig<IRevenueAgency>[] = revenueAgencyListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = REVENUE_AGENCY_PAGE_SORT_FIELDS.includes(configItem.key as IRevenueAgencySortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('RevenueAgencyListTable:: ', { revenueAgencyListTableConfig, revenueAgencyListTableConfigSortable, items })
	const tableConfig = revenueAgencyListTableConfigSortable

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}